import { EntitlementData } from './api.models';

export interface ErrorBlock {
  status: number;
  statusText: string;
  body?: unknown | null;
}

import { logger } from '../lib/logger';

/**
 * AuthService class provides methods to interact with the Backend API endpoints.
 */
class AuthService {
  private endpoints = {
    entitlements: (_userId: string, _tenantId: string): string =>
      `/api/authz/user/entitlements/${_userId}?tenantId=${_tenantId}`
  };

  /**
   * Creates an instance of AuthService.
   */
  constructor() {}

  /**
   * Fetches data from the given endpoint.
   * Prepends the base URL dynamically if necessary.
   * @param endpoint - The API endpoint to fetch data from.
   * @returns A promise that resolves to the fetched data.
   * @throws An error if the network response is not ok.
   */
  async fetchData<T>(endpoint: string): Promise<T> {
    const baseUrl = window.location.origin; // Dynamically get the base URL
    const fullUrl = `${baseUrl}${endpoint}`;

    // Log the request details
    logger.log(`API_FETCH_START: Requesting data from: ${fullUrl}`);
    try {
      const response = await fetch(fullUrl, {
        // do not send cookies to REST endpoints
        // also means fetch response will not receive cookies
        credentials: 'omit'
      });
      if (!response.ok) {
        //Building a structured error object
        const error: ErrorBlock = {
          status: response.status,
          statusText: response.statusText,
          body: null
        };
        logger.log(
          `API_FETCH_FAILURE: Failed to fetch data from: ${fullUrl}, Status: ${response.status} ${response.statusText}`
        );

        try {
          error.body = await response.json(); //if backend sends JSON error
        } catch {
          error.body = null;
          throw error; // throw globally here
        }
        logger.log(
          `API_FETCH_FAILURE: Failed to fetch data from: ${fullUrl}`,
          error
        );
        throw error;
      }
      const data = await response.json();
      //Log the response data
      logger.log(
        `API_FETCH_SUCCESS: Response from ${fullUrl}: ${JSON.stringify(data)}`
      );
      return data;
    } finally {
      logger.log('API_FETCH_COMPLETED');
    }
  }

  /**
   * Fetches Entitlements data for a given user ID and tenant ID from the API.
   * @param userId - The user ID.
   * @param tenantId - The tenant ID.
   * @returns A promise that resolves to the scan data.
   */

  getEntitlements(userId: string, tenantId: string) {
    return this.fetchData<EntitlementData>(
      this.endpoints.entitlements(userId, tenantId)
    );
  }
}

export default AuthService;
